import React, { useState, useEffect, useCallback } from 'react';
import { 
  FiX, 
  FiBook, 
  FiPlus, 
  FiTrash2, 
  FiUsers, 
  FiAlertCircle, 
  FiCheckCircle, 
  FiRefreshCw,
  FiSettings,
  FiMail,
  FiChevronDown,
  FiSearch,
  FiInfo
} from 'react-icons/fi';
import api from '../../../api';

const FacultyAssignmentModal = ({
  isOpen,
  onClose,
  classId,
  className,
  subjects = [],
  onAssignmentUpdated,
  notify
}) => {
  const [loading, setLoading] = useState(false);
  const [subjectFaculty, setSubjectFaculty] = useState({});
  const [assignments, setAssignments] = useState({});
  const [processingAssignments, setProcessingAssignments] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedSubjects, setExpandedSubjects] = useState(new Set());
  const [viewMode, setViewMode] = useState('all'); // 'all', 'assigned', 'unassigned'

  // Debug logging
  useEffect(() => {
    if (isOpen) {
      console.log('🎯 FacultyAssignmentModal opened with:', {
        classId,
        className,
        subjectsCount: subjects?.length || 0
      });
    }
  }, [isOpen, classId, className, subjects]);

  // Memoize fetchExistingAssignments and fetchFacultyForAllSubjects
  const fetchExistingAssignments = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/classManager/faculty-assignments/${classId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const { facultyAssignments = [] } = response.data;
      const existingMap = {};
      
      if (Array.isArray(facultyAssignments)) {
        facultyAssignments.forEach(assignment => {
          const { facultyId, facultyName, assignedAt, subjects = [] } = assignment;
          subjects.forEach(subjectAssignment => {
            const { subjectId } = subjectAssignment;
            const key = `${subjectId}-${facultyId}`;
            existingMap[key] = {
              assigned: true,
              role: subjectAssignment.role || 'Primary Teacher',
              facultyName: facultyName || 'Unknown Teacher',
              assignedAt: assignedAt || new Date().toISOString()
            };
          });
        });
      }

      setAssignments(existingMap);
    } catch (err) {
      console.error('❌ Error fetching existing assignments:', err);
      setAssignments({});
    }
  }, [classId]);

  const fetchFacultyForAllSubjects = useCallback(async () => {
    setLoading(true);
    const facultyData = {};
    
    try {
      const token = localStorage.getItem('token');

      for (const subject of subjects) {
        if (!subject || !subject._id) continue;

        try {
          const subjectTenantId = subject.tenantId || localStorage.getItem('tenantId');
          if (!subjectTenantId) continue;

          const response = await api.get(
            `/api/subjects/get-faculty-by-subject/${subject._id}/${subjectTenantId}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          
          facultyData[subject._id] = {
            faculty: response.data.faculty || [],
            subjectInfo: {
              name: subject.name,
              code: subject.subjectCode,
              isLab: subject.isLab || false,
              isElective: subject.isElective || false
            }
          };
        } catch (err) {
          facultyData[subject._id] = {
            faculty: [],
            subjectInfo: {
              name: subject.name,
              code: subject.subjectCode,
              isLab: subject.isLab || false,
              isElective: subject.isElective || false
            },
            error: err.message
          };
        }
      }

      setSubjectFaculty(facultyData);
    } catch (err) {
      console.error('❌ Error loading faculty data:', err);
      notify('Failed to load teachers list', 'error');
    } finally {
      setLoading(false);
    }
  }, [subjects, notify]);

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen && subjects?.length > 0) {
      fetchFacultyForAllSubjects();
      fetchExistingAssignments();
    }
  }, [isOpen, subjects, fetchFacultyForAllSubjects, fetchExistingAssignments]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setSubjectFaculty({});
      setAssignments({});
      setProcessingAssignments(new Set());
      setSearchTerm('');
      setExpandedSubjects(new Set());
      setViewMode('all');
    }
  }, [isOpen]);

  // For labs, return all assigned faculties; for others, return only one
  const getAssignedFacultyForSubject = (subjectId, isLab = false) => {
    if (isLab) {
      return Object.keys(assignments)
        .filter(key => {
          const [subjId] = key.split('-');
          return subjId === subjectId && assignments[key]?.assigned;
        })
        .map(key => {
          const [, facultyId] = key.split('-');
          return { facultyId, assignmentData: assignments[key] };
        });
    } else {
      const assignedKey = Object.keys(assignments).find(key => {
        const [subjId] = key.split('-');
        return subjId === subjectId && assignments[key]?.assigned;
      });
      if (assignedKey) {
        const [, facultyId] = assignedKey.split('-');
        return [{ facultyId, assignmentData: assignments[assignedKey] }];
      }
      return [];
    }
  };

  const assignFacultyToClass = async (subjectId, facultyId, role = 'Primary Teacher') => {
    const subjectObj = subjects.find(s => s._id === subjectId);
    if (subjectObj?.isElective) {
      notify('Teacher allocation for elective subjects is managed in Elective Group settings.', 'warning');
      return;
    }

    if (!subjectObj?.isLab) {
      const existingAssignments = getAssignedFacultyForSubject(subjectId, false);
      if (existingAssignments && existingAssignments.length > 0) {
        const assignedName = existingAssignments[0]?.assignmentData?.facultyName || 'a teacher';
        notify(`This subject already has ${assignedName} assigned.`, 'warning');
        return;
      }
    } else {
      const existingAssignments = getAssignedFacultyForSubject(subjectId, true);
      if (existingAssignments.some(af => af.facultyId === facultyId)) {
        notify('This teacher is already assigned to this lab.', 'warning');
        return;
      }
    }

    const assignmentKey = `${subjectId}-${facultyId}`;
    setProcessingAssignments(prev => new Set([...prev, assignmentKey]));

    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      const subjectObj = subjects.find(s => s._id === subjectId);
      const tenantId = subjectObj?.tenantId || localStorage.getItem('tenantId');

      const requestData = {
        classId,
        facultyId,
        subjects: [{ subjectId, role }],
        assignedBy: userId,
        tenantId
      };

      await api.post('/api/classManager/assign-faculty', requestData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const subjectName = subjectFaculty[subjectId]?.subjectInfo?.name || 'Subject';
      const facultyData = subjectFaculty[subjectId];
      const availableFaculty = facultyData?.faculty || [];
      const facultyObj = availableFaculty.find(f => (f.userId || f._id) === facultyId);
      const facultyName = facultyObj
        ? ((facultyObj.firstName || '') + ' ' + (facultyObj.lastName || '')).trim() || facultyObj.name || 'Teacher'
        : 'Teacher';

      notify(`✅ ${facultyName} linked to ${subjectName} successfully!`, 'success');
      
      setAssignments(prev => ({
        ...prev,
        [assignmentKey]: { 
          role, 
          assigned: true,
          facultyName,
          assignedAt: new Date().toISOString()
        }
      }));

      if (onAssignmentUpdated) onAssignmentUpdated();

    } catch (err) {
      console.error('❌ Error assigning faculty:', err);
      const errorMessage = err.response?.data?.message || 'Failed to assign teacher';
      notify(`❌ ${errorMessage}`, 'error');
    } finally {
      setProcessingAssignments(prev => {
        const newSet = new Set(prev);
        newSet.delete(assignmentKey);
        return newSet;
      });
    }
  };

  const removeFacultyFromClass = async (subjectId, facultyId) => {
    const assignmentKey = `${subjectId}-${facultyId}`;
    setProcessingAssignments(prev => new Set([...prev, assignmentKey]));

    try {
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');

      await api.delete('/api/classManager/remove-faculty', {
        data: { classId, facultyId, subjectId, removedBy: userId },
        headers: { Authorization: `Bearer ${token}` }
      });

      const facultyName = assignments[assignmentKey]?.facultyName || 'Teacher';
      const subjectName = subjectFaculty[subjectId]?.subjectInfo?.name || 'Subject';
      notify(`✅ ${facultyName} removed from ${subjectName} successfully!`, 'success');
      
      setAssignments(prev => {
        const updated = { ...prev };
        delete updated[assignmentKey];
        return updated;
      });

      if (onAssignmentUpdated) onAssignmentUpdated();

    } catch (err) {
      console.error('❌ Error removing faculty:', err);
      const errorMessage = err.response?.data?.message || 'Failed to remove teacher';
      notify(`❌ ${errorMessage}`, 'error');
    } finally {
      setProcessingAssignments(prev => {
        const newSet = new Set(prev);
        newSet.delete(assignmentKey);
        return newSet;
      });
    }
  };

  // Calculate statistics
  const getAssignmentStats = () => {
    const nonElectiveSubjects = uniqueSubjects.filter(subject => !subject.isElective);
    const totalSubjects = nonElectiveSubjects.length;
    const assignedSubjects = nonElectiveSubjects.filter(subject => {
      const res = getAssignedFacultyForSubject(subject._id, subject.isLab);
      return res && res.length > 0;
    }).length;
    
    return {
      totalSubjects,
      assignedSubjects,
      unassignedSubjects: totalSubjects - assignedSubjects,
      completionPercentage: totalSubjects > 0 ? Math.round((assignedSubjects / totalSubjects) * 100) : 0
    };
  };

  const uniqueSubjectsMap = {};
  subjects.forEach(subject => {
    if (!subject) return;
    const rawId = subject._id || subject.id || subject.subjectId;
    if (!rawId) return;
    const key = String(rawId);
    if (!uniqueSubjectsMap[key]) uniqueSubjectsMap[key] = subject;
  });
  const uniqueSubjects = Object.values(uniqueSubjectsMap);

  // Filter and process unique subjects
  const filteredSubjects = uniqueSubjects.filter(subject => {
    if (!subject || !subject._id) return false;
    
    const matchesSearch = !searchTerm ||
      subject.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subject.subjectCode?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const assignedList = getAssignedFacultyForSubject(subject._id, subject.isLab);
    const hasAssignment = assignedList && assignedList.length > 0;

    const matchesViewMode = 
      viewMode === 'all' ||
      (viewMode === 'assigned' && hasAssignment) ||
      (viewMode === 'unassigned' && !hasAssignment);
    
    return matchesSearch && matchesViewMode;
  });

  const stats = getAssignmentStats();

  if (!isOpen) return null;

  return (
    /* Top tier overlay safety structure - z-[99999] overrides layout shells completely */
    <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-md flex items-end sm:items-center justify-center z-[99999] overflow-hidden font-sans antialiased">
      
      {/* Container: Bottom drawer interface layout on mobile viewports */}
      <div className="bg-slate-50 w-full sm:max-w-4xl h-[94vh] sm:h-auto sm:max-h-[90vh] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200 transition-all duration-300">
        
        {/* Touch Swipe drag aesthetic handle decoration */}
        <div className="w-12 h-1 bg-slate-300 rounded-full mx-auto my-2.5 shrink-0 sm:hidden"></div>

        {/* Modal Header block - Compressed layout structure for mobile efficiency */}
        <div className="px-4 pb-3 pt-1 sm:p-5 bg-white border-b border-slate-200 shrink-0">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center space-x-2.5 min-w-0 flex-1">
              <div className="w-9 h-9 bg-indigo-50 rounded-xl flex items-center justify-center shrink-0 border border-indigo-100 hidden sm:flex">
                <FiUsers className="text-indigo-600 text-base" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-sm sm:text-base font-bold text-slate-900 tracking-tight truncate">Teacher Allocation</h1>
                
                {/* Mobile View: Inline compressed metric flex labels */}
                <div className="flex items-center gap-2 flex-wrap mt-0.5">
                  <span className="text-slate-500 text-xs font-semibold truncate max-w-[120px] sm:max-w-none">{className}</span>
                  <div className="flex items-center gap-1.5 sm:hidden text-[10px] font-bold bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200/40">
                    <span className="text-slate-600">Total: {stats.totalSubjects}</span>
                    <span className="text-slate-300">&bull;</span>
                    <span className="text-emerald-600">Done: {stats.assignedSubjects}</span>
                    <span className="text-slate-300">&bull;</span>
                    <span className="text-amber-600">{stats.completionPercentage}%</span>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-lg border border-slate-200 text-slate-400 hover:text-slate-600 hover:bg-slate-50 transition-all shrink-0"
            >
              <FiX className="text-base" />
            </button>
          </div>

          {/* Desktop Only: Full-sized grid stats block display */}
          <div className="hidden sm:grid grid-cols-3 gap-2 bg-slate-50 p-2 rounded-xl border border-slate-200/60 text-center mt-4">
            <div>
              <div className="text-xs font-bold text-slate-800">{stats.totalSubjects}</div>
              <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Total</div>
            </div>
            <div className="border-x border-slate-200">
              <div className="text-xs font-bold text-emerald-600">{stats.assignedSubjects}</div>
              <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Linked</div>
            </div>
            <div>
              <div className="text-xs font-bold text-amber-600">{stats.unassignedSubjects}</div>
              <div className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Pending</div>
            </div>
          </div>

          {/* Desktop Only: Progress Bar metric line wrapper */}
          {stats.totalSubjects > 0 && (
            <div className="mt-3 hidden sm:block">
              <div className="w-full bg-slate-100 rounded-full h-1.5 overflow-hidden">
                <div 
                  className="bg-indigo-600 h-1.5 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${stats.completionPercentage}%` }}
                ></div>
              </div>
            </div>
          )}
        </div>

        {/* Filters and Search Input row control items */}
        <div className="bg-white border-b border-slate-200 p-2.5 shrink-0 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 text-sm" />
            <input
              type="text"
              placeholder="Search subjects..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-3 py-1.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-medium focus:outline-none focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-500 placeholder-slate-400"
            />
          </div>

          <div className="flex items-center justify-between sm:justify-end gap-2">
            {/* Filter Pill Tab item components */}
            <div className="flex p-0.5 bg-slate-100 border border-slate-200 rounded-xl shrink-0">
              {['all', 'assigned', 'unassigned'].map((mode) => (
                <button
                  key={mode}
                  onClick={() => setViewMode(mode)}
                  className={`px-2.5 py-1 text-[11px] font-bold rounded-lg transition-all capitalize tracking-tight ${
                    viewMode === mode 
                      ? 'bg-white shadow-xs text-indigo-600' 
                      : 'text-slate-500 hover:text-slate-800'
                  }`}
                >
                  {mode === 'all' ? 'All' : mode === 'assigned' ? 'Linked' : 'Pending'}
                </button>
              ))}
            </div>

            <button
              onClick={fetchFacultyForAllSubjects}
              disabled={loading}
              className="flex items-center justify-center p-1.5 bg-slate-50 border border-slate-200 rounded-xl hover:bg-slate-100 disabled:opacity-50 text-slate-600 transition-colors"
              title="Refresh lists"
            >
              <FiRefreshCw className={`text-xs ${loading ? 'animate-spin text-indigo-600' : ''}`} />
            </button>
          </div>
        </div>

        {/* Content workflow view area box */}
        <div className="flex-1 overflow-y-auto p-2.5 sm:p-4 space-y-2 bg-slate-50">
          {loading ? (
            <div className="flex flex-col items-center justify-center py-16 bg-white rounded-2xl border border-slate-200 space-y-3 shadow-xs">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-indigo-600 border-t-transparent"></div>
              <p className="text-xs font-semibold text-slate-500">Syncing department rosters...</p>
            </div>
          ) : !subjects || subjects.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 p-6 shadow-xs">
              <FiBook className="text-slate-300 text-3xl mx-auto mb-2" />
              <h3 className="text-xs font-bold uppercase text-slate-700 tracking-wide">No Subjects Found</h3>
              <p className="text-xs text-slate-400 max-w-xs mx-auto mt-1">Please map formal syllabus structures to this active classroom build sequence.</p>
            </div>
          ) : filteredSubjects.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-slate-200 p-6 shadow-xs">
              <FiSearch className="text-slate-300 text-3xl mx-auto mb-2" />
              <h3 className="text-xs font-bold text-slate-700">No matches matched parameters</h3>
              <p className="text-xs text-slate-400 mt-1">Try modifying your filter state options or search input string.</p>
            </div>
          ) : (
            <div className="space-y-2 pb-6">
              {filteredSubjects.map((subject) => {
                if (!subject || !subject._id) return null;

                const facultyData = subjectFaculty[subject._id];
                const availableFaculty = facultyData?.faculty || [];
                const hasError = facultyData?.error;
                const assignedFacultyList = getAssignedFacultyForSubject(subject._id, subject.isLab);
                const isExpanded = expandedSubjects.has(subject._id);
                
                return (
                  <div key={subject._id} className="bg-white border border-slate-200/80 rounded-xl overflow-hidden transition-all shadow-xs">
                    <div 
                      className={`p-3 cursor-pointer transition-colors flex items-center justify-between gap-3 ${
                        isExpanded ? 'bg-slate-50/60 border-b border-slate-100' : 'hover:bg-slate-50/40'
                      }`}
                      onClick={() => setExpandedSubjects(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(subject._id)) newSet.delete(subject._id);
                        else newSet.add(subject._id);
                        return newSet;
                      })}
                    >
                      <div className="flex items-center space-x-2.5 min-w-0 flex-1">
                        <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
                          assignedFacultyList.length > 0 ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {assignedFacultyList.length > 0 ? (
                            <FiCheckCircle size={14} />
                          ) : subject.isLab ? (
                            <FiSettings size={14} />
                          ) : (
                            <FiBook size={14} />
                          )}
                        </div>
                        
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-1.5 flex-wrap">
                            <span className="text-xs font-bold text-slate-900 truncate">
                              {subject.name || 'Unnamed Course Unit'}
                            </span>
                            <span className="text-[9px] font-mono font-bold text-slate-400 bg-slate-50 border border-slate-200 px-1.5 py-0.2 rounded">
                              {subject.subjectCode || 'N/A'}
                            </span>
                          </div>

                          <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                            {subject.isLab && (
                              <span className="bg-amber-50 text-amber-700 text-[9px] font-bold px-1.5 py-0.2 rounded">Lab</span>
                            )}
                            {subject.isElective && (
                              <span className="bg-purple-50 text-purple-700 text-[9px] font-bold px-1.5 py-0.2 rounded">Elective</span>
                            )}
                            {assignedFacultyList.length > 0 && (
                              <span className="text-[11px] text-emerald-600 font-medium truncate max-w-[180px] sm:max-w-none">
                                Linked: {assignedFacultyList.map(f => f.assignmentData.facultyName).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center space-x-1.5 shrink-0">
                        <span className="text-[10px] bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded-full font-bold">
                          {availableFaculty.length}
                        </span>
                        <FiChevronDown className={`text-slate-400 text-xs transition-transform duration-200 ${
                          isExpanded ? 'rotate-180' : ''
                        }`} />
                      </div>
                    </div>

                    {isExpanded && (
                      <div className="bg-slate-50/40 p-2.5 border-t border-slate-100">
                        {subject.isElective ? (
                          <div className="flex items-start text-xs font-medium text-purple-900 bg-purple-50 border border-purple-100 rounded-xl p-2.5">
                            <FiInfo className="mr-2 shrink-0 text-purple-500 mt-0.5" size={13} />
                            <span>Elective workflows must be balanced via core cluster properties.</span>
                          </div>
                        ) : hasError ? (
                          <div className="p-3 text-center space-y-1">
                            <FiAlertCircle className="text-rose-400 mx-auto" size={18} />
                            <p className="text-xs font-semibold text-rose-700">Sync connection timed out.</p>
                            <button
                              onClick={fetchFacultyForAllSubjects}
                              className="px-2.5 py-1 bg-white border border-rose-200 text-rose-600 rounded-lg text-[10px] font-bold"
                            >
                              Retry sync
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {assignedFacultyList.length > 0 && (
                              <div className="bg-white border border-slate-200/60 rounded-xl p-2 space-y-1">
                                <div className="text-[9px] uppercase tracking-wider font-bold text-slate-400 px-1">Currently assigned</div>
                                {assignedFacultyList.map((assignedFaculty) => {
                                  const facultyKey = `${subject._id}-${assignedFaculty.facultyId}`;
                                  const isProcessing = processingAssignments.has(facultyKey);

                                  return (
                                    <div key={assignedFaculty.facultyId} className="flex items-center justify-between p-1.5 bg-emerald-50/50 rounded-lg border border-emerald-100 gap-2">
                                      <div className="min-w-0 flex-1">
                                        <p className="text-xs font-bold text-emerald-900 truncate">{assignedFaculty.assignmentData.facultyName}</p>
                                        <p className="text-[10px] text-emerald-600 font-medium">{assignedFaculty.assignmentData.role}</p>
                                      </div>
                                      <button
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          removeFacultyFromClass(subject._id, assignedFaculty.facultyId);
                                        }}
                                        disabled={isProcessing}
                                        className="w-7 h-7 flex items-center justify-center border border-rose-200 text-rose-600 hover:bg-rose-50 rounded-lg transition-colors bg-white shadow-2xs shrink-0"
                                      >
                                        {isProcessing ? (
                                          <FiRefreshCw className="animate-spin text-xs" />
                                        ) : (
                                          <FiTrash2 size={12} />
                                        )}
                                      </button>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {(!subject.isLab && assignedFacultyList.length > 0) ? null : availableFaculty.length > 0 ? (
                              <div className="space-y-1">
                                <div className="text-[9px] uppercase tracking-wider font-bold text-slate-400 px-1 pt-0.5">Assign options</div>
                                <div className="space-y-1 max-h-40 overflow-y-auto pr-1">
                                  {availableFaculty.map((faculty) => {
                                    const facultyId = faculty.userId || faculty._id;
                                    if (!facultyId) return null;

                                    const assignmentKey = `${subject._id}-${facultyId}`;
                                    const isProcessing = processingAssignments.has(assignmentKey);
                                    const isAssigned = assignedFacultyList.some(af => af.facultyId === facultyId);
                                    const facultyName = ((faculty.firstName || '') + ' ' + (faculty.lastName || '')).trim() || faculty.name || 'Unknown';

                                    if (isAssigned) return null;

                                    return (
                                      <div key={facultyId} className="bg-white border border-slate-200 rounded-lg p-2 flex items-center justify-between gap-3 shadow-2xs">
                                        <div className="min-w-0 flex-1">
                                          <p className="text-xs font-bold text-slate-800 truncate">{facultyName}</p>
                                          <p className="text-[10px] text-slate-400 truncate flex items-center gap-1">
                                            <FiMail size={10} className="shrink-0" />
                                            <span className="truncate">{faculty.email || 'No email attached'}</span>
                                          </p>
                                        </div>

                                        <button
                                          onClick={() => assignFacultyToClass(subject._id, facultyId, 'Primary Teacher')}
                                          disabled={isProcessing || (!subject.isLab && assignedFacultyList.length > 0)}
                                          className="flex items-center gap-1 px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 text-white disabled:text-slate-400 font-bold text-[10px] uppercase tracking-wider rounded-lg transition-all shrink-0"
                                        >
                                          {isProcessing ? (
                                            <FiRefreshCw className="animate-spin text-xs" />
                                          ) : (
                                            <>
                                              <FiPlus size={11} />
                                              <span>Link</span>
                                            </>
                                          )}
                                        </button>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            ) : (
                              <p className="text-center py-3 text-[11px] font-medium text-slate-400 bg-white border border-slate-100 rounded-xl">No active faculty profile entries matched.</p>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Modal Bottom buttons block */}
        <div className="bg-white border-t border-slate-200 p-2.5 sm:p-4 shrink-0 flex flex-col sm:flex-row items-center justify-between gap-2">
          <div className="text-[11px] font-semibold text-slate-500 text-center sm:text-left hidden sm:block">
            Allocation Metric Balance: {stats.assignedSubjects}/{stats.totalSubjects} ({stats.completionPercentage}%)
          </div>
          
          <div className="grid grid-cols-2 sm:flex sm:items-center gap-2 w-full sm:w-auto">
            <button
              onClick={() => setExpandedSubjects(new Set(subjects.map(s => s._id).filter(Boolean)))}
              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200/80 rounded-xl text-xs font-bold transition-colors text-center"
            >
              Expand All
            </button>
            <button
              onClick={() => setExpandedSubjects(new Set())}
              className="px-2.5 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200/80 rounded-xl text-xs font-bold transition-colors text-center"
            >
              Collapse
            </button>
            <button
              onClick={onClose}
              className="col-span-2 sm:col-span-1 px-5 py-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-xl text-xs font-bold uppercase tracking-wider shadow-sm transition-all text-center"
            >
              Done
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default FacultyAssignmentModal;